import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { FaSpotify, FaAmazon } from "react-icons/fa";
import { SiIheartradio, SiYoutubemusic, SiApplepodcasts } from "react-icons/si";
import { Link } from "react-router-dom";
import { ASSETS, PROJECTS, BOOKS, BRAND, PODCAST as PODCAST_DATA } from "../data/siteContent";
import { SiteLayout } from "../layouts/SiteLayout";
import { HERO, HOME, NEWSLETTER } from "../constants/siteTestIds";
import { NewsletterInline } from "../components/site/NewsletterInline";
import { CinematicQuote } from "../components/site/CinematicQuote";
import { Seo } from "../seo/Seo";
import { buildSchemas, podcastSchema, book12LawsSchema, breadcrumbSchema, webPageSchema } from "../seo/schemas";

const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`bg-white py-24 md:py-36 border-t border-black/10 ${className}`}>
    <div className="mx-auto max-w-[1600px] px-6 md:px-12">{children}</div>
  </section>
);

const SectionLabel = ({ num, label, title, italic, kicker, action }) => (
  <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
    <div className="col-span-12 md:col-span-3">
      <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">
        <span className="text-[#C8102E]">{num}</span> — {label}
      </p>
    </div>
    <div className="col-span-12 md:col-span-9">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <h2 className="font-display text-[#0a0a0a] text-[clamp(32px,5vw,68px)] leading-[1.02] tracking-[-0.02em]">
          {title}
          {italic && <><br /><span className="font-light text-black/45">{italic}</span></>}
        </h2>
        {action}
      </div>
      {kicker && (
        <p className="mt-6 max-w-2xl text-black/65 text-base md:text-lg leading-relaxed">{kicker}</p>
      )}
    </div>
  </div>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroPortraitY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <SiteLayout>
      <Seo
        title="Manuel Santana — Media Personality · Podcast Host · Author"
        description="Manuel Santana is an American media personality, podcast host of The Prove Your Point Podcast, and author of The 12 Laws of Dating Dynamics."
        path="/"
        schemas={buildSchemas([
          podcastSchema,
          book12LawsSchema,
          webPageSchema({ path: "/", name: "Manuel Santana", description: "Official site of Manuel Santana." }),
          breadcrumbSchema([{ name: "Home", path: "/" }]),
        ])}
      />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen bg-white overflow-hidden">
        <div className="absolute top-[88px] left-0 right-0 z-20 px-6 md:px-12 flex justify-between font-mono-ui text-[10px] tracking-[0.3em] text-black/40 uppercase">
          <span>{BRAND.homeCity}</span>
          <span className="hidden md:inline">{BRAND.domain}</span>
        </div>

        <div className="relative mx-auto max-w-[1600px] px-6 md:px-12 pt-32 md:pt-40 pb-20 grid grid-cols-12 gap-6 min-h-screen">
          <motion.div style={{ y: heroTextY, opacity: heroOpacity }} className="col-span-12 lg:col-span-7 flex flex-col justify-center relative z-10 will-change-transform">
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="font-mono-ui text-[11px] tracking-[0.3em] text-[#0a0a0a] uppercase mb-8"
            >
              — {BRAND.tagline}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }}
              className="font-display text-[#0a0a0a] text-[clamp(56px,11vw,180px)] leading-[0.85] tracking-[-0.03em]"
            >
              Manuel<br /><span className="font-light">Santana</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-10 max-w-xl text-black/65 text-lg md:text-xl leading-relaxed"
            >
              Host of The Prove Your Point Podcast. Author of The 12 Laws of
              Dating Dynamics. A media personality building conversation around
              modern relationships, culture and identity.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-12 flex flex-wrap items-center gap-5"
            >
              <Link to="/contact" data-testid={HERO.primaryCta} className="btn-primary">
                Get In Touch <ArrowRight size={16} />
              </Link>
              <Link to="/podcast" data-testid={HERO.secondaryCta} className="link-underline font-mono-ui text-[12px] tracking-[0.25em] uppercase text-[#0a0a0a]">
                Listen To The Podcast
              </Link>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: heroPortraitY }} className="col-span-12 lg:col-span-5 relative flex items-end justify-center lg:justify-end will-change-transform">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }}
              className="portrait-frame w-[88%] sm:w-[70%] lg:w-full max-w-[640px] aspect-[4/5]"
            >
              <img src={ASSETS.heroPortrait} alt="Manuel Santana" draggable={false} />
            </motion.div>
          </motion.div>

          <div data-testid={HERO.scrollHint} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-black/40 scroll-pulse">
            <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase">Scroll</span>
            <ArrowDown size={14} />
          </div>
        </div>
      </section>

      {/* PODCAST PROMO — no fake episode data */}
      <Section id="podcast-promo">
        <SectionLabel
          num="01"
          label="Podcast"
          title="The Prove Your"
          italic="Point Podcast."
          kicker="Long conversations on relationships, culture and the things we won't say out loud. New episodes on Spotify, Apple Podcasts and YouTube."
        />
        <FadeIn>
          <div className="grid grid-cols-12 gap-6 lg:gap-12 items-center">
            <div className="col-span-12 lg:col-span-5">
              <div className="portrait-frame w-full max-w-[560px] aspect-[4/5]">
                <img src={ASSETS.podcastPortrait} alt="The Prove Your Point Podcast" draggable={false} />
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <h3 className="font-display text-[#0a0a0a] text-3xl md:text-5xl tracking-tight leading-tight">
                Listen wherever <span className="font-light text-black/45">you already are.</span>
              </h3>
              <p className="mt-6 text-black/65 text-base md:text-lg leading-relaxed max-w-xl">
                Weekly long-form conversations, hosted by Manuel Santana.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={PODCAST_DATA.spotify} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-black/15 hover:border-[#1DB954] px-4 py-3 transition-all">
                  <FaSpotify className="text-[#1DB954]" size={16} />
                  <span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase">Spotify</span>
                </a>
                <a href={PODCAST_DATA.apple} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-black/15 hover:border-[#9933CC] px-4 py-3 transition-all">
                  <SiApplepodcasts className="text-[#9933CC]" size={16} />
                  <span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase">Apple Podcasts</span>
                </a>
                <a href={PODCAST_DATA.iheart} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-black/15 hover:border-[#C6002B] px-4 py-3 transition-all">
                  <SiIheartradio className="text-[#C6002B]" size={16} />
                  <span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase">iHeart</span>
                </a>
                <a href={PODCAST_DATA.amazonMusic} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-black/15 hover:border-[#00A8E1] px-4 py-3 transition-all">
                  <FaAmazon className="text-[#00A8E1]" size={16} />
                  <span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase">Amazon Music</span>
                </a>
                <a href={PODCAST_DATA.youtubeMusic} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-black/15 hover:border-[#FF0000] px-4 py-3 transition-all">
                  <SiYoutubemusic className="text-[#FF0000]" size={18} />
                  <span className="font-mono-ui text-[11px] tracking-[0.2em] uppercase">YouTube Music</span>
                </a>
              </div>
              <Link to="/podcast" className="mt-10 inline-flex link-underline font-mono-ui text-[12px] tracking-[0.25em] uppercase">
                Open The Podcast Page →
              </Link>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* CINEMATIC LIFESTYLE QUOTE */}
      <CinematicQuote
        image={ASSETS.bookingPortrait}
        overline="— A note from the host"
        quote="I'm not here to make you feel good. I'm here to make you tell yourself the truth — once, and for the rest of your life."
        attribution="Manuel Santana"
      />

      {/* ABOUT */}
      <Section id="about-snippet">
        <SectionLabel num="02" label="About Manuel" title="Not a guru." italic="A signal in the noise." />
        <div className="grid grid-cols-12 gap-6 lg:gap-12 items-start">
          <div className="col-span-12 lg:col-span-5">
            <FadeIn>
              <div className="portrait-frame w-full max-w-[520px] aspect-[4/5]">
                <img src={ASSETS.aboutPortrait} alt="Manuel Santana portrait" draggable={false} />
              </div>
            </FadeIn>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-8">
            <FadeIn delay={0.15}>
              <p className="font-display font-light text-[#0a0a0a] text-2xl md:text-3xl leading-snug">
                “I'm not here to make you feel good. I'm here to make you tell yourself the truth — once, and for the rest of your life.”
              </p>
            </FadeIn>
            <FadeIn delay={0.25}>
              <p className="mt-10 text-black/65 text-base md:text-lg leading-relaxed">
                Manuel Santana is an American media personality, podcast host
                and author. Splitting his time between {BRAND.cities.join(", ")}, his work
                explores modern relationships, culture and identity — without
                flattening either side of the conversation for clicks.
              </p>
            </FadeIn>
            <FadeIn delay={0.35}>
              <Link to="/about" data-testid={HOME.aboutCta} className="mt-8 inline-flex btn-ghost">
                Read The Full Story <ArrowRight size={16} />
              </Link>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* BOOKS */}
      <Section id="books-snippet">
        <SectionLabel
          num="03"
          label="Books"
          title="The 12 Laws of"
          italic="Dating Dynamics."
          kicker="Manuel's 2025 release — a modern manual for adults navigating dating, partnership and standards."
          action={
            <Link to="/books" data-testid={HOME.booksCta} className="link-underline font-mono-ui text-[12px] tracking-[0.25em] uppercase">
              All Books →
            </Link>
          }
        />
        <FadeIn>
          <div className="grid grid-cols-12 gap-6 lg:gap-12 items-center">
            <div className="col-span-12 lg:col-span-5">
              <div className="portrait-frame w-full max-w-[520px] aspect-[4/5]">
                <img src={ASSETS.bookPortrait} alt={BOOKS[0].title} draggable={false} />
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8 space-y-6">
              <span className="font-mono-ui text-[11px] tracking-[0.3em] text-black/45">{BOOKS[0].released}</span>
              <p className="font-display font-light text-[#0a0a0a] text-2xl md:text-3xl leading-snug">{BOOKS[0].subtitle}</p>
              <p className="text-black/65 text-base md:text-lg leading-relaxed">{BOOKS[0].description}</p>
              {BOOKS[0].officialSite && (
                <a
                  href={BOOKS[0].officialSite}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-3 bg-[#C8102E] hover:bg-[#0a0a0a] text-white px-6 py-4 mt-2 transition-all"
                >
                  <span className="font-mono-ui text-[11px] tracking-[0.25em] uppercase">Visit The Book Site</span>
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </a>
              )}
              <div className="flex flex-wrap gap-3 pt-2">
                {BOOKS[0].purchaseLinks.slice(0, 3).map((p) => (
                  <a key={p.label} href={p.href} target="_blank" rel="noreferrer" className="btn-ghost">{p.label}</a>
                ))}
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* PROJECTS */}
      <Section id="projects-snippet">
        <SectionLabel
          num="04"
          label="Projects"
          title="The wider"
          italic="ecosystem."
          kicker="Beyond the podcast and the books — the things Manuel builds when the mic is off."
        />
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          {PROJECTS.map((p, i) => {
            const external = p.href.startsWith("http");
            const cardClass = "flex flex-col h-full border border-black/15 hover:border-[#C8102E] p-7 md:p-8 transition-all";
            const inner = (
              <>
                <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">{p.role}</p>
                <h3 className="mt-5 font-display text-[#0a0a0a] text-2xl md:text-3xl tracking-tight">{p.name}</h3>
                <p className="mt-4 text-black/60 text-sm leading-relaxed flex-1">{p.summary}</p>
                <span className="mt-8 self-start inline-flex items-center gap-2 bg-[#0a0a0a] hover:bg-[#C8102E] text-white px-4 py-2.5 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition-colors">
                  {p.cta} <ArrowRight size={12} />
                </span>
              </>
            );
            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="col-span-12 md:col-span-6 lg:col-span-3"
              >
                {external ? (
                  <a href={p.href} target="_blank" rel="noreferrer" className={cardClass}>{inner}</a>
                ) : (
                  <Link to={p.href} className={cardClass}>{inner}</Link>
                )}
              </motion.div>
            );
          })}
        </div>
        <div className="mt-12">
          <Link to="/projects" data-testid={HOME.projectsCta} className="link-underline font-mono-ui text-[12px] tracking-[0.25em] uppercase">
            View All Projects →
          </Link>
        </div>
      </Section>

      {/* NEWSLETTER */}
      <NewsletterInline />

      {/* CONTACT CTA */}
      <Section id="contact-snippet">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">06 — Contact</p>
            <h2 className="mt-6 font-display text-[#0a0a0a] text-[clamp(40px,7vw,96px)] leading-[0.95] tracking-[-0.02em]">
              Let's talk.<br/>
              <span className="font-light text-black/45">Briefly &amp; well.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:text-right">
            <Link to="/contact" data-testid={HOME.contactCta} className="btn-primary">
              Open Contact Page <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </Section>
    </SiteLayout>
  );
}
